import { EMPTY_GUID } from "@utils/helpers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getInteractionSelector } from "redux/memoizedSelectors";
import { TouchpointSelectCard } from "shared/components/cards/cx-cards/TouchpointSelectCard";
import EditModeSwitch from "shared/components/edit-mode-switch";
import GenericTypeahead from "shared/dropdowns/GenericTypeahead";
import { MenuItem, MenuLabel } from "shared/dropdowns/typeahead-styles";
import { SwitchProvider } from "shared/dynamic-imports";
import { FlexColumnGap } from "shared/ui-basics";

export default function TouchpointDropdown({ object, link, unlink, attributeName, showInUse = false, isSwitchDisabled, stageId, isCloseWhenSelected = false }) {
	const { t } = useTranslation();
	const { cjmIdStr ,personaIdStr} = useLocation().search.getAllMapsParams();

	const allTouchpoints = useSelector((state) => state.libraries.touchPoint) || [];
	const inUseTouchpointsIds = useSelector((state) => state?.maps?.current_map?.cjmData)
		?.flat()
		?.map((interaction) => interaction?.touchPointId) || [];

	const touchpointCategories = [{ name: "ALL", id: EMPTY_GUID }, ...(useSelector((state) => state.libraries.touchPointCategories) || [])];
	const linkedIds = object?.[attributeName] || object?.linkedTouchpoints || [];
	const linkedTouchpoints = allTouchpoints?.filter((tp) => linkedIds?.includes(tp?.id));
	const inUseTouchpoints = allTouchpoints?.filter((tp) => inUseTouchpointsIds?.includes(tp.id));
	const isUseStageTouchpoints = useSelector(getInteractionSelector(personaIdStr, stageId));

	const renderInUseTouchpoints = (close) => {
		return (
			<div>
				<MenuLabel className="my-2">{t("ALREADY_USED")}</MenuLabel>
				<FlexColumnGap gap="8">
					{inUseTouchpoints?.map((item) => (
						<MenuItem key={item?.id} onClick={() => { link(item.id); isCloseWhenSelected && close() }}>
							<TouchpointSelectCard object={item} disabled={linkedIds?.includes(item?.id)} />
						</MenuItem>
					))}
				</FlexColumnGap>
			</div>
		);
	};

	const renderMenuWithCategories = ({ results, clearInput, close }) => {
		return touchpointCategories.slice(1, Infinity)?.map((category) => {
			const categoryList = results?.filter((l) => l.categoryId === category.id && category.isShown);
			if (categoryList?.length === 0) return null;
			return (
				<div key={category?.id}>
					<MenuLabel className="my-2">{t(category?.name)}</MenuLabel>
					<FlexColumnGap gap="8">
						{categoryList?.map((item) => (
							<MenuItem
								key={item?.id}
								onClick={() => {
									clearInput();
									link(item.id);
									isCloseWhenSelected && close();
								}}>
								<TouchpointSelectCard object={item} disabled={linkedIds?.includes(item?.id)} />
							</MenuItem>
						))}
					</FlexColumnGap>
				</div>
			);
		});
	};

	const renderReadOnlyItems = () => (
		<>
			{linkedTouchpoints?.map((tp) => (
				<div key={tp?.id}>
					<TouchpointSelectCard object={tp} showBorders={false} info={cjmIdStr && stageId && !isUseStageTouchpoints?.find((interaction) => interaction.touchPointId === tp.id)} />
				</div>
			))}
		</>
	);

	const params = {
		selected: linkedTouchpoints,
		options: allTouchpoints,
		renderSelectedItem: (tp) => <TouchpointSelectCard key={tp.id} object={tp} handleRemove={unlink} isEdit={true} showBorders={false} info={cjmIdStr && stageId && !isUseStageTouchpoints.find((interaction) => interaction.touchPointId === tp.id)} />,
		renderCustomMenu: ({ results, input, clearInput, close }) => {
			return (
				<div style={{ height: "300px", overflowY: "auto" }}>
					{showInUse && inUseTouchpoints && !input && renderInUseTouchpoints(close)}
					{renderMenuWithCategories({ results, clearInput, close })}
				</div>
			);
		},
		filter: ({ query, option }) => {
			const tp = allTouchpoints.find((tp) => tp.id === option?.id);
			if (tp && !linkedTouchpoints.map((g) => g.id)?.includes(option?.id)) return tp?.name?.toLowerCase().includes(query?.toLowerCase());
		},
	};

	return (
		<SwitchProvider isDisabled={isSwitchDisabled}>
			<EditModeSwitch addLabel="ADD_TOUCHPOINT" renderReadOnlyItems={renderReadOnlyItems} isEmpty={linkedTouchpoints?.length === 0} isDisabled={isSwitchDisabled}>
				<GenericTypeahead {...params} />
			</EditModeSwitch>
		</SwitchProvider>
	);
}
